import React, { useState } from "react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Textarea } from "./components/ui/textarea";
import { Card, CardContent } from "./components/ui/card";
import { ArrowRight, ArrowLeft, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import "./MultiStepForm.css";

const tipiIntervento = [
  "Sottotetto",
  "Pareti (intercapedini)",
  "Sottotetto + Pareti"
];

function MultiStepForm() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    // Step 1
    tipoIntervento: "",
    // Step 2
    nome: "",
    telefono: "",
    email: "",
    // Step 3
    provincia: "",
    note: ""
  });

  const handleChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const nextStep = () => {
    if (step === 1 && !formData.tipoIntervento) {
      toast.error("Seleziona il tipo di intervento");
      return;
    }
    if (step === 2) {
      if (!formData.nome || !formData.telefono || !formData.email) {
        toast.error("Compila tutti i campi obbligatori");
        return;
      }
    }
    setStep(step + 1);
  };

  const prevStep = () => setStep(step - 1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.provincia) {
      toast.error("Inserisci la provincia");
      return;
    }

    setLoading(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      
      const response = await axios.post(`${API_URL}/api/contatti/`, formData, {
        timeout: 20000
      });
      
      if (response.data) {
        toast.success("✅ Richiesta inviata! Ti contatteremo entro 24 ore.");
        
        // Reset
        setFormData({
          tipoIntervento: "",
          nome: "",
          telefono: "",
          email: "",
          provincia: "",
          note: ""
        });
        setStep(1);
        
        // Scroll top
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error) {
      console.error("Errore:", error);
      toast.error("📞 Chiama il Numero Verde: 800.86.46.21");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="multistep-form">
      <div className="progress-bar">
        <div className={`progress-step ${step >= 1 ? 'active' : ''}`}>
          <div className="step-circle">1</div>
          <span>Intervento</span>
        </div>
        <div className={`progress-line ${step >= 2 ? 'active' : ''}`}></div>
        <div className={`progress-step ${step >= 2 ? 'active' : ''}`}>
          <div className="step-circle">2</div>
          <span>Contatti</span>
        </div>
        <div className={`progress-line ${step >= 3 ? 'active' : ''}`}></div>
        <div className={`progress-step ${step >= 3 ? 'active' : ''}`}>
          <div className="step-circle">3</div>
          <span>Dettagli</span>
        </div>
      </div>

      <Card className="step-card">
        <CardContent>
          {step === 1 && (
            <div className="step-content">
              <h3>Che tipo di isolamento ti interessa?</h3>
              <div className="intervento-grid">
                {tipiIntervento.map(tipo => (
                  <button
                    key={tipo}
                    type="button"
                    className={`intervento-card ${formData.tipoIntervento === tipo ? 'selected' : ''}`}
                    onClick={() => handleChange('tipoIntervento', tipo)}
                  >
                    <CheckCircle className={`check-icon ${formData.tipoIntervento === tipo ? 'visible' : ''}`} />
                    <span>{tipo}</span>
                  </button>
                ))}
              </div>
              <Button onClick={nextStep} size="lg" className="btn-next">
                Avanti
                <ArrowRight className="ml-2" />
              </Button>
            </div>
          )}

          {step === 2 && (
            <div className="step-content">
              <h3>I tuoi dati di contatto</h3>
              <div className="form-fields">
                <div className="form-group">
                  <Label htmlFor="nome">Nome Completo *</Label>
                  <Input
                    id="nome"
                    value={formData.nome}
                    onChange={(e) => handleChange('nome', e.target.value)}
                    placeholder="Mario Rossi"
                    required
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="telefono">Telefono *</Label>
                  <Input
                    id="telefono"
                    type="tel"
                    value={formData.telefono}
                    onChange={(e) => handleChange('telefono', e.target.value)}
                    placeholder="333 123 4567"
                    required
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="mario.rossi@email.it"
                    required
                  />
                </div>
              </div>
              <div className="btn-group">
                <Button onClick={prevStep} variant="outline" size="lg">
                  <ArrowLeft className="mr-2" />
                  Indietro
                </Button>
                <Button onClick={nextStep} size="lg" className="btn-next">
                  Avanti
                  <ArrowRight className="ml-2" />
                </Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="step-content">
              <h3>Ultimi dettagli</h3>
              <form onSubmit={handleSubmit}>
                <div className="form-fields">
                  <div className="form-group">
                    <Label htmlFor="provincia">Provincia *</Label>
                    <Input
                      id="provincia"
                      value={formData.provincia}
                      onChange={(e) => handleChange('provincia', e.target.value)}
                      placeholder="VR, BS, MN, TN..."
                      required
                    />
                  </div>
                  <div className="form-group">
                    <Label htmlFor="note">Note aggiuntive (opzionale)</Label>
                    <Textarea
                      id="note"
                      value={formData.note}
                      onChange={(e) => handleChange('note', e.target.value)}
                      placeholder="Raccontaci di più sul tuo progetto..."
                      rows={3}
                    />
                  </div>
                </div>
                <div className="btn-group">
                  <Button type="button" onClick={prevStep} variant="outline" size="lg">
                    <ArrowLeft className="mr-2" />
                    Indietro
                  </Button>
                  <Button type="submit" size="lg" className="btn-submit" disabled={loading}>
                    {loading ? "Invio..." : "Invia Richiesta"}
                    {!loading && <ArrowRight className="ml-2" />}
                  </Button>
                </div>
              </form>
            </div>
          )}
        </CardContent>
      </Card>

      <p className="privacy-text">
        Inviando la richiesta accetti la nostra privacy policy
      </p>
    </div>
  );
}

export default MultiStepForm;
